import { Link } from "@tanstack/react-router";
import { ArrowRight, Check, ShieldCheck } from "lucide-react";
import type { Locale } from "@/lib/i18n";
import { quotePath, quoteSearch } from "@/lib/quote-links";

export const MIGRATION_FAQS = {
  en: [
    {
      q: "Do we need Shopify Plus to work with Shugert on a migration?",
      a: "No. We migrate established stores to standard Shopify and Shopify Plus. The destination is selected from operating requirements such as B2B, checkout extensibility, multiple stores or markets, and complex integrations, not as a default upsell.",
    },
    {
      q: "Which platforms can you migrate to Shopify from?",
      a: "Typical source platforms include WooCommerce, Magento, BigCommerce, PrestaShop, custom ecommerce platforms, and legacy Shopify builds. The final scope depends on the data model, catalog, orders, customers, integrations, content, URLs, and storefront requirements.",
    },
    {
      q: "Can you show comparable migrations?",
      a: "Yes. Published migration work includes Petwell (Magento → Shopify Plus, 10,000+ SKUs), Llantas Cavazos (custom/legacy MX platform → Shopify, 8,000+ active SKUs), PokerPower (legacy stack → Shopify, full replatform), and MP Health (Squarespace → Shopify with Shopify POS). Their recorded outcomes and project boundaries are summarized on this page and documented in /work. They describe those engagements, not a forecast for another store.",
    },
    {
      q: "How do you protect SEO during a Shopify migration?",
      a: "The migration scope includes URL ownership, a documented 301 redirect map, canonical rules, metadata and structured-data parity, crawl validation, analytics continuity, and post-launch Google Search Console monitoring. These controls are designed to protect existing SEO equity and make problems diagnosable after cutover. They do not guarantee zero ranking volatility; expected monitoring, ownership, and the response plan are defined before launch.",
    },
    {
      q: "What is included in the migration project?",
      a: "A typical project covers discovery, destination architecture, catalog and customer data, order-history decisions, redirects, theme or storefront implementation, apps and integrations, analytics, QA, cutover, and a stabilization period. Assumptions and exclusions are written before implementation.",
    },
    {
      q: "How much does a Shopify migration cost?",
      a: "Entry and bounded replatform projects start from $5,000. That starting point is not the average price of an enterprise Magento migration. Scope changes with catalog size, data quality, URL count, storefront requirements, integrations, subscriptions, international requirements, SEO exposure, and launch coordination. Shopify Plus projects involving ERP, B2B, or multi-store architecture follow a separate architecture path.",
    },
  ],
  es: [
    {
      q: "¿Necesitamos Shopify Plus para trabajar con Shugert en una migración?",
      a: "No. Migramos tiendas establecidas a Shopify estándar y a Shopify Plus. El destino se elige según requisitos operativos como B2B, checkout extensibility, múltiples tiendas o mercados e integraciones complejas; no como upsell automático.",
    },
    {
      q: "¿Desde qué plataformas pueden migrar a Shopify?",
      a: "Las plataformas de origen habituales incluyen WooCommerce, Magento, BigCommerce, PrestaShop, ecommerce custom y tiendas Shopify legacy. El alcance final depende del modelo de datos, catálogo, pedidos, clientes, integraciones, contenido, URLs y storefront.",
    },
    {
      q: "¿Cómo protegen el SEO durante una migración a Shopify?",
      a: "El alcance incluye ownership de URLs, mapa de redirects, reglas canonical, paridad de metadatos y schema, validación de rastreo, continuidad de analítica y monitoreo post-launch. Nadie puede prometer cero volatilidad, pero los controles técnicos y el plan de respuesta deben quedar escritos antes del cutover.",
    },
    {
      q: "¿Qué incluye el proyecto de migración?",
      a: "Normalmente incluye discovery, arquitectura destino, catálogo y clientes, decisión sobre historial de pedidos, redirects, theme o storefront, apps e integraciones, analítica, QA, cutover y estabilización. Los supuestos y exclusiones se escriben antes de implementar.",
    },
    {
      q: "¿Cuánto cuesta una migración a Shopify?",
      a: "Las migraciones entran por el proyecto Custom Build or Replatform. La inversión cambia según catálogo, calidad de datos, alcance del storefront, integraciones, suscripciones, internacionalización, riesgo SEO y coordinación de lanzamiento. Entregamos un rango acotado después de revisar el sistema actual.",
    },
  ],
} as const;

const COPY = {
  en: {
    path: "/shopify-migration",
    eyebrow: "Shopify Migration Agency",
    title: "Migrate to Shopify without losing control of data, SEO, or launch risk.",
    intro:
      "Shugert is a Shopify Select Partner migration agency (since 2015) for established DTC and B2B brands in the United States, Spain, and LatAm. We migrate established ecommerce stores from WooCommerce, Magento, BigCommerce, PrestaShop, custom platforms, and legacy Shopify builds, into standard Shopify or Shopify Plus according to the operation you actually need.",
    answer:
      "The destination plan is a technical and operating decision. Standard Shopify is enough for many established brands. Shopify Plus is reserved for requirements such as B2B, complex Markets structures, checkout extensibility, Functions, multiple stores, or enterprise integrations.",
    primary: "Scope my Shopify migration",
    secondary: "Review migration resources",
    trust: ["Shopify Partner since 2015", "30+ complex migrations recorded", "Senior engineering ownership", "SEO and launch controls included"],
    sources: {
      eyebrow: "Source platforms",
      title: "The migration plan starts with what must be preserved, not with a preferred destination tier.",
      body: "We inventory the commercial and operational behavior of the current platform before deciding what should move, change, or be retired.",
      items: ["WooCommerce", "Magento", "BigCommerce", "PrestaShop", "Custom ecommerce", "Legacy Shopify"],
    },
    scope: {
      eyebrow: "End-to-end scope",
      title: "One accountable migration path from discovery through stabilization.",
      rows: [
        ["Architecture and inventory", "Catalog, variants, content, customers, orders, URLs, apps, integrations, analytics, and operational dependencies."],
        ["Data and content migration", "Mapping, transformation, import validation, exceptions, ownership, and reconciliation decisions."],
        ["Storefront implementation", "Theme or custom storefront, merchandising surfaces, accessibility, performance, and content workflows."],
        ["SEO continuity", "URL mapping, redirects, canonicals, metadata, structured data, internal links, crawl validation, and Search Console monitoring."],
        ["Integrations and tracking", "Payments, subscriptions, ERP/PIM/WMS/3PL where applicable, customer events, GA4, and release observability."],
        ["QA, cutover, stabilization", "Representative scenarios, launch authority, rollback decisions, production validation, and post-launch triage."],
      ],
    },
    destinations: {
      eyebrow: "Choose the right destination",
      title: "Shopify and Shopify Plus are two operating paths inside the same migration discipline.",
      intro: "We recommend the least complex platform tier that can responsibly support the roadmap.",
      standard: {
        label: "Shopify migration",
        title: "Standard Shopify",
        body: "For established brands that need a reliable storefront, catalog, apps, subscriptions, international selling, and integrations without enterprise-only platform requirements.",
        points: ["Strong DTC storefront", "Theme and app ecosystem", "Markets for typical international setups", "Lower platform overhead", "Can upgrade later when requirements justify it"],
        cta: "Scope a Shopify migration",
      },
      plus: {
        label: "Premium path",
        title: "Shopify Plus migration",
        body: "For operations where platform capabilities and governance justify the higher tier and a more complex implementation program.",
        points: ["B2B companies and catalogs", "Checkout extensibility and Functions", "Complex Markets or multi-store structures", "ERP, PIM, WMS, OMS or 3PL dependencies", "Higher release and stakeholder complexity"],
        cta: "Review Shopify Plus engineering",
      },
    },
    sequence: {
      eyebrow: "Delivery sequence",
      title: "A controlled migration is a sequence of decisions, not a bulk import followed by launch day.",
      steps: [
        ["01", "Discovery and risk map", "Confirm source-of-truth systems, scope boundaries, data quality, SEO exposure, integrations, and launch constraints."],
        ["02", "Architecture and written scope", "Define the Shopify destination, data contracts, URL ownership, storefront implementation, milestones, and acceptance criteria."],
        ["03", "Build and migration rehearsals", "Implement in staging, run representative imports, validate scenarios, and resolve exceptions before production cutover."],
        ["04", "Launch and stabilization", "Execute the cutover plan, validate production, monitor commercial and technical signals, and close or transfer ownership."],
      ],
    },
    evidence: {
      eyebrow: "Delivery record · not a guarantee",
      title: "Migration capability connected to implementation, SEO, and release ownership.",
      body: "Shugert records more than 750 Shopify engagements, 150 full builds, and 30 complex migrations across 35 markets. These figures establish delivery breadth; a new migration still depends on its data, integrations, catalog, review process, and launch conditions.",
      points: ["Platform and SEO decisions live in the same scope", "Named work and migration outcomes are available in the portfolio", "Client code, data, accounts, and vendor relationships remain client-owned", "Limitations and unresolved risks are documented before launch"],
    },
    resources: {
      eyebrow: "Migration resources",
      title: "Review the method before commissioning the migration.",
      rows: [
        ["Migration resource hub", "Guides by source platform, SEO risk, timing, and platform decision.", "/resources/migration"],
        ["Shopify migration guide", "The generic how-to checklist for data, storefront, SEO, QA, cutover, and launch.", "/resources/shopify-migration-guide-2026"],
        ["Shopify migration timeline", "Duration, phase dependencies, acceptance gates, and launch-window planning.", "/resources/shopify-migration-timeline"],
        ["Shopify replatforming playbook", "Program governance, cutover command, rollback, and 90-day stabilization.", "/resources/shopify-replatforming-playbook"],
        ["WooCommerce to Shopify", "A platform-specific migration guide for catalog, URLs, apps, and launch.", "/resources/woocommerce-to-shopify-migration"],
        ["Shopify Plus migration", "The premium path when B2B, checkout, Markets, Functions, or enterprise operations apply.", "/resources/shopify-plus-migration"],
      ],
    },
    faqTitle: "Questions before planning a migration to Shopify.",
    finalTitle: "Plan the Shopify migration around the operation, not around a platform upsell.",
    finalBody: "Share the current platform, catalog, integrations, SEO exposure, and target timeline. We will identify whether standard Shopify or Shopify Plus is the responsible destination and return the smallest useful scope.",
  },
  es: {
    path: "/es/migracion-shopify",
    eyebrow: "Ingeniería de migración a Shopify",
    title: "Migra a Shopify sin perder control de datos, SEO ni riesgo de lanzamiento.",
    intro:
      "Migramos tiendas ecommerce establecidas desde WooCommerce, Magento, BigCommerce, PrestaShop, plataformas custom y tiendas Shopify legacy hacia Shopify estándar o Shopify Plus, según la operación que realmente necesitas.",
    answer:
      "El plan destino es una decisión técnica y operativa. Shopify estándar es suficiente para muchas marcas establecidas. Shopify Plus se reserva para requisitos como B2B, estructuras complejas de Markets, checkout extensibility, Functions, múltiples tiendas o integraciones enterprise.",
    primary: "Cotizar mi migración a Shopify",
    secondary: "Revisar recursos de migración",
    trust: ["Shopify Partner desde 2015", "30+ migraciones complejas registradas", "Ownership de ingeniería senior", "Controles SEO y de lanzamiento incluidos"],
    sources: {
      eyebrow: "Plataformas de origen",
      title: "El plan comienza con lo que debe preservarse, no con un tier destino preferido.",
      body: "Inventariamos el comportamiento comercial y operativo de la plataforma actual antes de decidir qué se mueve, qué cambia y qué debe retirarse.",
      items: ["WooCommerce", "Magento", "BigCommerce", "PrestaShop", "Ecommerce custom", "Shopify legacy"],
    },
    scope: {
      eyebrow: "Alcance end-to-end",
      title: "Una ruta responsable desde discovery hasta estabilización.",
      rows: [
        ["Arquitectura e inventario", "Catálogo, variantes, contenido, clientes, pedidos, URLs, apps, integraciones, analítica y dependencias operativas."],
        ["Migración de datos y contenido", "Mapeo, transformación, importación, validación, excepciones, ownership y decisiones de conciliación."],
        ["Implementación de storefront", "Theme o storefront custom, merchandising, accesibilidad, performance y workflows de contenido."],
        ["Continuidad SEO", "Mapa de URLs, redirects, canonicals, metadata, schema, enlaces internos, validación de crawl y monitoreo en Search Console."],
        ["Integraciones y tracking", "Pagos, suscripciones, ERP/PIM/WMS/3PL cuando aplica, customer events, GA4 y observabilidad de release."],
        ["QA, cutover y estabilización", "Escenarios representativos, autoridad de launch, decisiones de rollback, validación en producción y triage post-launch."],
      ],
    },
    destinations: {
      eyebrow: "Elige el destino correcto",
      title: "Shopify y Shopify Plus son dos rutas operativas dentro de la misma disciplina de migración.",
      intro: "Recomendamos el tier menos complejo que pueda soportar responsablemente el roadmap.",
      standard: {
        label: "Migración a Shopify",
        title: "Shopify estándar",
        body: "Para marcas establecidas que necesitan un storefront confiable, catálogo, apps, suscripciones, venta internacional e integraciones sin requisitos exclusivos de una plataforma enterprise.",
        points: ["Storefront DTC sólido", "Ecosistema de themes y apps", "Markets para setups internacionales habituales", "Menor overhead de plataforma", "Upgrade posterior cuando los requisitos lo justifiquen"],
        cta: "Cotizar migración a Shopify",
      },
      plus: {
        label: "Ruta premium",
        title: "Migración a Shopify Plus",
        body: "Para operaciones donde las capacidades de plataforma y el gobierno de releases justifican el tier superior y un programa de implementación más complejo.",
        points: ["B2B, empresas y catálogos", "Checkout extensibility y Functions", "Markets complejos o múltiples tiendas", "Dependencias ERP, PIM, WMS, OMS o 3PL", "Mayor complejidad de release y stakeholders"],
        cta: "Revisar ingeniería Shopify Plus",
      },
    },
    sequence: {
      eyebrow: "Secuencia de delivery",
      title: "Una migración controlada es una secuencia de decisiones, no un import masivo seguido por el launch.",
      steps: [
        ["01", "Discovery y mapa de riesgo", "Confirmar sistemas source of truth, límites, calidad de datos, exposición SEO, integraciones y restricciones de lanzamiento."],
        ["02", "Arquitectura y alcance escrito", "Definir destino Shopify, contratos de datos, ownership de URLs, storefront, milestones y criterios de aceptación."],
        ["03", "Build y ensayos de migración", "Implementar en staging, ejecutar imports representativos, validar escenarios y resolver excepciones antes del cutover."],
        ["04", "Launch y estabilización", "Ejecutar el cutover, validar producción, monitorear señales comerciales y técnicas, y cerrar o transferir ownership."],
      ],
    },
    evidence: {
      eyebrow: "Historial de delivery · no es garantía",
      title: "Capacidad de migración conectada con implementación, SEO y ownership de release.",
      body: "Shugert registra más de 750 engagements Shopify, 150 builds completos y 30 migraciones complejas en 35 mercados. Estas cifras demuestran amplitud; una nueva migración sigue dependiendo de datos, integraciones, catálogo, revisiones y condiciones de lanzamiento.",
      points: ["Las decisiones de plataforma y SEO viven en el mismo alcance", "Hay trabajo identificado y resultados de migración en el portafolio", "Código, datos, cuentas y vendors permanecen en propiedad del cliente", "Limitaciones y riesgos pendientes se documentan antes del launch"],
    },
    resources: {
      eyebrow: "Recursos de migración",
      title: "Revisa el método antes de contratar la migración.",
      rows: [
        ["Hub de migración", "Guías por plataforma origen, riesgo SEO, timing y decisión de destino.", "/es/recursos/migracion"],
        ["Guía de migración a Shopify", "Checklist general para datos, storefront, SEO, QA, cutover y lanzamiento.", "/es/recursos/shopify-migration-guide-2026"],
        ["Cronograma de migración a Shopify", "Duración, dependencias, gates de aceptación y ventana de lanzamiento.", "/es/recursos/shopify-migration-timeline"],
        ["Playbook de replatforming a Shopify", "Gobierno del programa, comando de cutover, rollback y estabilización de 90 días.", "/es/recursos/playbook-replatforming-shopify"],
        ["WooCommerce a Shopify", "Guía específica para catálogo, URLs, apps y lanzamiento.", "/es/recursos/migracion-woocommerce-shopify"],
        ["Migración a Shopify Plus", "La ruta premium cuando aplican B2B, checkout, Markets, Functions u operación enterprise.", "/es/recursos/shopify-plus-migration"],
      ],
    },
    faqTitle: "Preguntas antes de planear una migración a Shopify.",
    finalTitle: "Planea la migración alrededor de la operación, no de un upsell de plataforma.",
    finalBody: "Comparte plataforma actual, catálogo, integraciones, exposición SEO y timeline. Identificaremos si Shopify estándar o Shopify Plus es el destino responsable y devolveremos el alcance útil mínimo.",
  },
} as const;

const NAMED_MIGRATIONS = [
  {
    client: "Petwell",
    route: "Magento → Shopify Plus",
    scale: "10,000+ SKUs",
    outcome: "8 weeks, zero downtime, customer/order history + redirect mapping",
  },
  {
    client: "Llantas Cavazos",
    route: "Custom/legacy MX platform → Shopify",
    scale: "8,000+ active SKUs",
    outcome: "Under 7.5 weeks, >90% organic traffic retained in week one, 84 size collections",
  },
  {
    client: "PokerPower",
    route: "Legacy stack → Shopify",
    scale: "Full replatform",
    outcome: "Phased cutover, 0 downtime, +24% page speed, SEO controls in scope",
  },
  {
    client: "MP Health",
    route: "Squarespace → Shopify",
    scale: "Clinic + POS",
    outcome: "4–6 weeks, multi-component IV inventory + Shopify POS",
  },
] as const;

function SectionHeader({ eyebrow, title, body }: { eyebrow: string; title: string; body?: string }) {
  return (
    <header className="max-w-3xl">
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#0a0a0a]">{eyebrow}</p>
      <h2 className="mt-4 text-balance font-display text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">{title}</h2>
      {body ? <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">{body}</p> : null}
    </header>
  );
}

export function MigrationMoneyPage({ locale }: { locale: Locale }) {
  const c = COPY[locale];
  const faqs = MIGRATION_FAQS[locale];
  const isEs = locale === "es";
  const quote = quotePath(locale);
  const quoteSearchParams = quoteSearch({ source: c.path, intent: "rebuild", challenge: "migrating" });
  const plusHref = isEs ? "/es/agencia-shopify-plus" : "/shopify-plus-agency";

  return (
    <article>
      <section className="money-page-hero relative overflow-hidden" data-surface="dark">
        <div className="absolute inset-0 bg-grid-faint opacity-20" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl gap-14 px-6 py-20 lg:grid-cols-12 lg:items-center lg:py-28">
          <div className="lg:col-span-8">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#0a0a0a]">{c.eyebrow}</p>
            <h1 className="mt-5 max-w-5xl text-balance font-display text-5xl font-semibold leading-[1.02] tracking-tight text-foreground sm:text-6xl lg:text-7xl">{c.title}</h1>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-muted-foreground sm:text-xl">{c.intro}</p>
            <p className="mt-6 max-w-3xl border-l-2 border-[#0a0a0a]/30 pl-5 text-sm leading-relaxed text-foreground/82 sm:text-base">{c.answer}</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to={quote} search={quoteSearchParams} className="btn-lift inline-flex items-center gap-2 rounded-full bg-foreground px-7 py-3.5 text-sm font-semibold text-background">
                {c.primary}<ArrowRight className="h-4 w-4" />
              </Link>
              <a href={isEs ? "/es/recursos/migracion" : "/resources/migration"} className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3.5 text-sm font-semibold text-foreground hover:border-[#0a0a0a]/40">{c.secondary}</a>
            </div>
            <ul className="mt-9 flex flex-wrap gap-x-6 gap-y-3 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {c.trust.map((item) => <li key={item} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#0a0a0a]" />{item}</li>)}
            </ul>
          </div>
          <aside className="premium-panel rounded-[1.75rem] border border-[#0a0a0a]/20 p-7 lg:col-span-4">
            <ShieldCheck className="h-6 w-6 text-[#0a0a0a]" />
            <p className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-[#0a0a0a]">{c.sources.eyebrow}</p>
            <h2 className="mt-3 font-display text-2xl font-semibold leading-tight text-foreground">{c.sources.title}</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{c.sources.body}</p>
            <ul className="mt-6 grid grid-cols-2 gap-3">
              {c.sources.items.map((item) => <li key={item} className="border-t border-[#0a0a0a]/20 pt-3 text-sm font-medium text-foreground">{item}</li>)}
            </ul>
          </aside>
        </div>
      </section>

      {!isEs ? (
        <section className="mx-auto max-w-7xl px-6 py-14 sm:py-16" aria-labelledby="named-migrations-title">
          <div className="max-w-4xl">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#0a0a0a]">Migration evidence</p>
            <h2 id="named-migrations-title" className="mt-4 text-balance font-display text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl">Named migrations, not a gallery.</h2>
            <p className="mt-5 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">These are named replatforming engagements with the source platform, operating scale, and recorded delivery outcome visible on this page.</p>
          </div>
          <div className="mt-10 overflow-x-auto border-y border-border">
            <table className="w-full min-w-[860px] border-collapse text-left">
              <thead>
                <tr className="border-b border-border text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  <th className="px-4 py-4">Client</th>
                  <th className="px-4 py-4">Source → destination</th>
                  <th className="px-4 py-4">Scale</th>
                  <th className="px-4 py-4">Outcome</th>
                  <th className="px-4 py-4">Case</th>
                </tr>
              </thead>
              <tbody>
                {NAMED_MIGRATIONS.map((item) => (
                  <tr key={item.client} className="border-b border-border last:border-b-0">
                    <td className="px-4 py-5 font-display text-lg font-semibold text-foreground">{item.client}</td>
                    <td className="px-4 py-5 text-sm text-foreground/85">{item.route}</td>
                    <td className="px-4 py-5 text-sm text-foreground/85">{item.scale}</td>
                    <td className="px-4 py-5 text-sm leading-relaxed text-muted-foreground">{item.outcome}</td>
                    <td className="px-4 py-5"><a href="/work" className="text-sm font-semibold text-foreground underline underline-offset-4">View in /work</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-5 text-sm leading-relaxed text-muted-foreground">Published metrics describe the cited engagement. They are not forecasts for another store. <a href="/work" className="font-semibold text-foreground underline underline-offset-4">Full decision record: /work</a></p>
        </section>
      ) : null}

      <section className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
        <SectionHeader eyebrow={c.scope.eyebrow} title={c.scope.title} />
        <div className="mt-12 border-y border-border">
          {c.scope.rows.map((row, index) => (
            <div key={row[0]} className="grid gap-4 border-b border-border py-7 last:border-b-0 md:grid-cols-[4rem_0.75fr_1.25fr]">
              <span className="font-mono text-xs text-[#0a0a0a]">0{index + 1}</span>
              <h3 className="font-display text-xl font-semibold text-foreground">{row[0]}</h3>
              <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">{row[1]}</p>
            </div>
          ))}
        </div>
        {!isEs ? (
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm font-semibold">
            <a href="/work" className="underline underline-offset-4 hover:opacity-80">Petwell Magento to Shopify Plus</a>
            <a href="/work" className="underline underline-offset-4 hover:opacity-80">Llantas Cavazos 8000 SKU migration</a>
            <a href="/work" className="underline underline-offset-4 hover:opacity-80">PokerPower SEO cutover</a>
            <a href="/work" className="underline underline-offset-4 hover:opacity-80">MP Health Squarespace to Shopify</a>
          </div>
        ) : null}
      </section>

      <section className="authority-section" data-surface="dark">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
          <SectionHeader eyebrow={c.destinations.eyebrow} title={c.destinations.title} body={c.destinations.intro} />
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <article className="rounded-3xl border border-[#0a0a0a]/25 bg-background p-7 sm:p-9">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0a0a0a]">{c.destinations.standard.label}</p>
              <h3 className="mt-4 font-display text-3xl font-semibold text-foreground">{c.destinations.standard.title}</h3>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{c.destinations.standard.body}</p>
              <ul className="mt-7 space-y-3">{c.destinations.standard.points.map((item) => <li key={item} className="flex gap-2 text-sm text-foreground/82"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0a0a0a]" />{item}</li>)}</ul>
              <Link to={quote} search={quoteSearchParams} className="mt-8 inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-sm font-semibold text-background">{c.destinations.standard.cta}<ArrowRight className="h-4 w-4" /></Link>
            </article>
            <article className="rounded-3xl border border-[#1f1f1f]/30 bg-[#1f1f1f]/[0.045] p-7 sm:p-9 [&_*]:!text-[#0a0a0a]">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] opacity-70">{c.destinations.plus.label}</p>
              <h3 className="mt-4 font-display text-3xl font-semibold">{c.destinations.plus.title}</h3>
              <p className="mt-4 text-sm leading-relaxed opacity-85">{c.destinations.plus.body}</p>
              <ul className="mt-7 space-y-3">{c.destinations.plus.points.map((item) => <li key={item} className="flex gap-2 text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0" />{item}</li>)}</ul>
              <a href={plusHref} className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#0a0a0a]/40 px-6 py-3 text-sm font-semibold">{c.destinations.plus.cta}<ArrowRight className="h-4 w-4" /></a>
            </article>

          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
        <SectionHeader eyebrow={c.sequence.eyebrow} title={c.sequence.title} />
        <ol className="mt-12 grid gap-6 md:grid-cols-2">
          {c.sequence.steps.map((step) => (
            <li key={step[0]} className="rounded-3xl border border-border p-7">
              <span className="font-mono text-xs text-[#0a0a0a]">{step[0]}</span>
              <h3 className="mt-5 font-display text-2xl font-semibold text-foreground">{step[1]}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{step[2]}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="authority-section" data-surface="dark">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-[0.75fr_1.25fr] lg:py-24">
          <div><p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#90a8ff]">{c.evidence.eyebrow}</p><h2 className="mt-4 font-display text-3xl font-semibold leading-tight sm:text-4xl">{c.evidence.title}</h2></div>
          <div><p className="text-base leading-relaxed text-white/72 sm:text-lg">{c.evidence.body}</p><ul className="mt-7 grid gap-3 sm:grid-cols-2">{c.evidence.points.map((item) => <li key={item} className="border-t border-white/20 pt-3 text-sm text-white/75">{item}</li>)}</ul><a href={isEs ? "/es/proyectos" : "/work"} className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-white">{isEs ? "Revisar proyectos" : "Review named work"}<ArrowRight className="h-4 w-4" /></a></div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20">
        <SectionHeader eyebrow={c.resources.eyebrow} title={c.resources.title} />
        <div className="mt-10 border-y border-border">{c.resources.rows.map((row) => <a key={row[2]} href={row[2]} className="group grid gap-3 border-b border-border py-6 last:border-b-0 md:grid-cols-[0.8fr_1.2fr_auto] md:items-center"><strong className="font-display text-lg text-foreground">{row[0]}</strong><p className="text-sm text-muted-foreground">{row[1]}</p><ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" /></a>)}</div>
      </section>

      <section className="authority-section" data-surface="dark">
        <div className="mx-auto max-w-4xl px-6 py-20">
          <SectionHeader eyebrow="FAQ" title={c.faqTitle} />
          <div className="mt-10 divide-y divide-border border-y border-border">{faqs.map((faq) => <details key={faq.q} className="group py-5"><summary className="flex cursor-pointer list-none items-center justify-between gap-5 font-medium text-foreground">{faq.q}<span className="text-xl text-muted-foreground transition-transform group-open:rotate-45">+</span></summary><p className="mt-4 text-sm leading-relaxed text-muted-foreground">{faq.a}</p></details>)}</div>
        </div>
      </section>

      <section className="money-final-cta mx-auto max-w-5xl px-6 py-20 text-center sm:py-24" data-surface="light">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0a0a0a]">{c.eyebrow}</p>
        <h2 className="mt-4 text-balance font-display text-4xl font-semibold text-foreground sm:text-5xl">{c.finalTitle}</h2>
        <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">{c.finalBody}</p>
        <Link to={quote} search={quoteSearchParams} className="btn-lift mt-8 inline-flex items-center gap-2 rounded-full bg-foreground px-7 py-3.5 text-sm font-semibold text-background">{c.primary}<ArrowRight className="h-4 w-4" /></Link>
      </section>
    </article>
  );
}
